import React from 'react';
import { Link } from 'react-router-dom';
import { Waves, Box, Thermometer, Droplets, Filter, ClipboardList, ListChecks, FileStack, Layers, FlaskConical, Combine, Ruler, FileCheck2 } from 'lucide-react';

// Phase 2/3 calculator types aren't built yet - shown as "coming soon" so the
// hub reflects the full intended scope without implying they work today.
//
// Note: the standalone Tank Sizing (tank_sizing) and Jacket/Limpet Coil
// (jacket_design) calculators have been merged into Tank Design
// (tank_design), which calls both under the hood based on a
// construction_type choice - see tank_design.py. Their pages/routes and
// calculator_type registrations still exist (existing saved calculations
// of those types still work), just no longer linked from this hub.
const CALCULATORS = [
  {
    type: 'pipe_pressure_drop',
    label: 'Pipe Pressure Drop',
    description: 'Darcy-Weisbach head loss for a distribution pipe run, including fittings and elevation.',
    icon: Waves,
    to: '/design/pipe-pressure-drop',
    available: true,
  },
  {
    type: 'pipe_sizing',
    label: 'Pipe Sizing',
    description: 'Recommends a pipe internal diameter from flow ÷ target velocity, by duty (feed/discharge, gravity/suction, backwash).',
    icon: Ruler,
    to: '/design/pipe-sizing',
    available: true,
  },
  {
    type: 'tank_design',
    label: 'Tank Design',
    description: 'ASME Section VIII tank sizing, optionally combined with jacket (annular) or limpet coil (half-pipe) design in one flow.',
    icon: Box,
    to: '/design/tank-design',
    available: true,
  },
  {
    type: 'heat_exchanger',
    label: 'Heat Exchanger',
    description: 'Shell & tube LMTD sizing: duty, area, tube count, bundle/shell diameter, utility flow.',
    icon: Thermometer,
    to: '/design/heat-exchanger',
    available: true,
  },
  {
    type: 'psf',
    label: 'Pressure Sand Filter (PSF)',
    description: 'Vessel sizing from service velocity, sand/gravel bed depth, and backwash hydraulics.',
    icon: Layers,
    to: '/design/psf',
    available: true,
  },
  {
    type: 'acf',
    label: 'Activated Carbon Filter (ACF)',
    description: 'EBCT-driven carbon bed sizing, cross-checked against max linear velocity.',
    icon: FlaskConical,
    to: '/design/acf',
    available: true,
  },
  {
    type: 'softener',
    label: 'Softener Sizing',
    description: 'Resin volume sizing from hardness load and regeneration cycle, plus salt dose.',
    icon: Droplets,
    to: '/design/softener',
    available: true,
  },
  {
    type: 'dm_plant',
    label: 'DM Plant',
    description: 'Two-bed (cation + anion) demineralization sizing, optional mixed-bed polisher.',
    icon: Combine,
    to: '/design/dm-plant',
    available: true,
  },
  {
    type: 'ro_system',
    label: 'RO System Sizing',
    description: 'Reverse osmosis rough sizing from feed water data.',
    icon: Droplets,
    available: false,
  },
  {
    type: 'uf_system',
    label: 'UF System Sizing',
    description: 'Ultrafiltration membrane area sizing.',
    icon: Filter,
    available: false,
  },
  {
    type: 'bom',
    label: 'Bill of Materials',
    description: 'Roll up calculator outputs and catalog parts into a costed BOM.',
    icon: ClipboardList,
    available: false,
  },
];

const DesignCalculatorsHubPage = () => {
  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Design Calculators</h1>
          <div className="flex items-center gap-3">
            <Link
              to="/design/project-summary"
              className="flex items-center gap-2 bg-white border border-komal-blue text-komal-blue hover:bg-blue-50 text-sm font-semibold px-4 py-2 rounded"
            >
              <FileStack size={16} />
              Project Design Summary
            </Link>
            <Link
              to="/design/calculations"
              className="flex items-center gap-2 bg-komal-blue hover:bg-komal-blue-dark text-white text-sm font-semibold px-4 py-2 rounded"
            >
              <ListChecks size={16} />
              Saved Calculations
            </Link>
            <Link
              to="/design/drawing-checks"
              className="flex items-center gap-2 bg-white border border-komal-blue text-komal-blue hover:bg-blue-50 text-sm font-semibold px-4 py-2 rounded"
            >
              <FileCheck2 size={16} />
              Drawing Checks
            </Link>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-6 max-w-3xl">
          Standalone engineering sizing tools. Every saved calculation starts
          as a draft and must be reviewed and approved by an engineer before
          its numbers should be used to manufacture anything.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CALCULATORS.map((calc) => {
            const Icon = calc.icon;
            const card = (
              <div
                className={`glass-card p-5 h-full flex flex-col gap-2 ${
                  calc.available ? 'hover:shadow-md transition-shadow cursor-pointer' : 'opacity-60'
                }`}
              >
                <div className="flex items-center gap-2 text-komal-blue">
                  <Icon size={20} />
                  <h2 className="font-semibold text-gray-900">{calc.label}</h2>
                </div>
                <p className="text-xs text-gray-500 flex-1">{calc.description}</p>
                {!calc.available && (
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Coming soon</span>
                )}
              </div>
            );
            return calc.available ? (
              <Link key={calc.type} to={calc.to}>{card}</Link>
            ) : (
              <div key={calc.type}>{card}</div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DesignCalculatorsHubPage;
